import { useEffect, useRef } from 'react';
import {
  GROUP_RENDER_TYPES,
  VALID_CALC_SOURCE_QUESTION_TYPES,
  TYPES,
} from './constants';
import {
  HouseholdMemberFieldEnum,
  MAPPING_TABLE_ORIENTATIONS,
  QuestionGroupFieldEnum,
} from 'src/redux/slices/ProntoHousingAdministration/MappingTableSliceConstants';
import {
  createMappingTableCellName,
  handleTableOverflowIndicators,
} from 'src/components/ProntoHousingAdministration/QuestionMapping/Navigation/MappingTab/MappingTableConfigModal/MappingTableUtils';

export const CUSTOM_DATA_KEY = 'custom-data';
export const TOOLTIP_TYPES = {
  qit: 'qit',
  sit: 'sit',
  sitDate: 'sitDate',
  sitName: 'sitName',
  sitHhmt: 'sitHhmt',
  sitPhone: 'sitPhone',
  mit: 'mit',
  calculation: 'calculation',
  multiQits: 'multiQits',
  tableCell: 'tableCell',
  hasAnswers: 'hasAnswers',
  matchAnswers: 'matchAnswers',
};

const DIVIDER = '###';

export const reduceQits = (acc, curr) => {
  let result = [];
  if (!curr?.contextId) {
    result = [...acc, curr];
  }

  if (curr?.contextId && curr?.qits?.length) {
    result = [...acc, ...curr.qits];
  }

  return result;
};

export function buildTooltipDom(data) {
  const container = document.createElement('div');
  const title = document.createElement('h2');

  container.classList.add('MappingTooltip');

  // title
  title.classList.add('MappingTooltip-title');
  title.innerText = data?.label;
  container.appendChild(title);

  for (const key in data?.fields) {
    if (data?.fields && key in data.fields && data.fields[key]?.value) {
      const field = data?.fields[key];
      const p = document.createElement('p');
      const strong = document.createElement('strong');
      const span = document.createElement('span');

      p.classList.add('MappingTooltip-field');
      strong.innerText = `${field?.name}: `;
      span.innerText = field?.value;

      p.appendChild(strong);
      p.appendChild(span);
      container.appendChild(p);
    }
  }

  return container;
}

export function getAnnotationName(qit) {
  if (!qit) {
    return null;
  }
  const forCompliance = qit?.for_compliance ? 'For Compliance' : 'For Resident';

  if (qit?.contextName) {
    return `${qit?.contextName}\n${qit.section.name} / ${qit.page.name} / ${qit.question_group.name}`;
  } else if (!qit?.contextName && qit?.question) {
    return `${qit.question.name}\n${qit.section.name} / ${qit.page.name} / ${qit.question_group.name}\n${forCompliance}`;
  }
}

export function buildTooltipData(data, type, householdMemberTypes = []) {
  if (!data || !type) {
    return null;
  }

  const tooltipData = { fields: {} };
  const isContext = Boolean(data?.question_context);

  if (type === TOOLTIP_TYPES.qit) {
    tooltipData.label = 'Questionnaire';

    if (isContext) {
      tooltipData.fields.contextName = {
        name: 'Context name',
        value: data?.question_context?.context_template?.name,
      };
    }

    if (!isContext) {
      tooltipData.fields.question = {
        name: 'Question',
        value: data?.qit?.question?.name,
      };
      tooltipData.fields.choices = {
        name: 'Choices',
        value: data?.qit?.question?.question_choices?.length > 0 && data?.text,
      };
    }

    if (data?.qit?.question?.question_choices?.length > 0 && 'blank' in data) {
      tooltipData.fields.blank = {
        name: 'Non-Selected',
        value: data?.blank ? 'Yes' : 'No',
      };
    }

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data?.question_render_type,
    };
    tooltipData.fields.section = {
      name: 'Section',
      value: data?.qit?.section?.name,
    };
    tooltipData.fields.page = { name: 'Page', value: data?.qit?.page?.name };
    tooltipData.fields.group = {
      name: 'Group',
      value: data?.qit?.question_group?.name,
    };
    tooltipData.fields.groupType = {
      name: 'Group Type',
      value: data?.qit?.question_group?.type,
    };

    if (!isContext) {
      tooltipData.fields.groupRenderType = {
        name: 'Group Render Type',
        value:
          GROUP_RENDER_TYPES[data?.question_group_render_type]?.displayName,
      };
      tooltipData.fields.groupRenderTypeChoice = {
        name: 'Group Render Type Choice',
        value: data?.question_group_render_type_choice,
      };
      tooltipData.fields.forCompliance = {
        name: 'For',
        value: data.qit.for_compliance ? 'Compliance' : 'Resident',
      };
      tooltipData.fields.hhmt = {
        name: 'Household Member Types',
        value: (data?.household_member_type || [])
          .map((el) => {
            const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
            return found?.display_name || found?.name || '';
          })
          .join(', '),
      };
    }
  }
  if (
    [
      TOOLTIP_TYPES.sit,
      TOOLTIP_TYPES.sitDate,
      TOOLTIP_TYPES.sitName,
      TOOLTIP_TYPES.sitHhmt,
      TOOLTIP_TYPES.sitPhone,
    ].includes(type)
  ) {
    tooltipData.label = 'eSign';
    tooltipData.fields.name = { name: 'Name', value: data?.sit?.name };
    tooltipData.fields.isDate = {
      name: 'Is signature date',
      value: data?.is_date ? 'Yes' : 'No',
    };
    tooltipData.fields.isName = {
      name: 'Is signature name',
      value: data?.is_name ? 'Yes' : 'No',
    };
    tooltipData.fields.isHhmt = {
      name: 'Is signature member type',
      value: data?.is_hhmt ? 'Yes' : 'No',
    };
    tooltipData.fields.isPhone = {
      name: 'Is signature phone number',
      value: data?.is_phone ? 'Yes' : 'No',
    };
    tooltipData.fields.signatureCollection = {
      name: 'Signature Collection',
      value: data?.sit?.signature_collection,
    };
    tooltipData.fields.signatureType = {
      name: 'Signature Type',
      value: data?.sit?.signature_type,
    };
    tooltipData.fields.compliance = {
      name: 'Compliance',
      value: data?.sit?.compliance ? 'Yes' : 'No',
    };
    tooltipData.fields.unassigned = {
      name: 'Unassigned',
      value: data?.sit?.unassigned ? 'Yes' : 'No',
    };
    tooltipData.fields.hhmt = {
      name: 'Needed Signature',
      value: (data?.sit?.needed_signatures || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || '';
        })
        .join(', '),
    };
  }
  if (type === TOOLTIP_TYPES.mit) {
    tooltipData.label = 'Miscellaneous';
    tooltipData.fields.name = { name: 'Field', value: data?.mit?.field_name };
    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data?.render_type,
    };

    let memberTypeNames = (data?.household_member_type || [])
      .map((el) => {
        const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
        return found?.display_name || found?.name || '';
      })
      .join(', ');
    if (!memberTypeNames && data?.mit?.field_name === 'member_type_count') {
      memberTypeNames = 'All';
    }
    tooltipData.fields.hhmt = {
      name: 'Household Member Types',
      value: memberTypeNames,
    };
  }

  if (type === TOOLTIP_TYPES.calculation) {
    const calc = data?.calculation;
    tooltipData.label = 'Calculation';
    tooltipData.fields.name = { name: 'Name', value: calc?.name };
    tooltipData.fields.type = { name: 'Type', value: calc?.type };
    tooltipData.fields.formula = { name: 'Formula', value: calc?.formula };
    tooltipData.fields.for_certification = {
      name: 'Household Calculation',
      value: calc?.for_certification ? 'Yes' : 'No',
    };
    if (!calc?.for_certification) {
      tooltipData.fields.hhmt = {
        name: 'Household Member Types',
        value: (data?.household_member_type || [])
          .map((el) => {
            const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
            return found?.display_name || found?.name || '';
          })
          .join(', '),
      };
    }
    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data?.render_type,
    };
    tooltipData.fields.groupRenderType = {
      name: 'Group Render Type',
      value: data?.question_group_render_type_choice,
    };
  }

  if (type === TOOLTIP_TYPES.multiQits) {
    tooltipData.label = 'Multiple Questions';
    const sections = data.text.split('\n\n');
    sections.map((section) => {
      const [name, ...questions] = section.split('\n');
      const sectionName = name.replace(':', '');
      tooltipData.fields[sectionName] = {
        name: sectionName,
        value: '\n' + questions.join('\n'),
      };
    });
  }

  if (type === TOOLTIP_TYPES.hasAnswers) {
    const hasAnswersConfig = data?.has_answers;
    tooltipData.label = 'Has Answers';

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data?.question_render_type,
    };

    tooltipData.fields.qitScope = {
      name: 'QIT Scope',
      value:
        hasAnswersConfig?.qit_scope === 'any'
          ? 'Any (OR logic)'
          : 'All (AND logic)',
    };

    const questionNames = (hasAnswersConfig?.qits || [])
      .map((q) => q.question_name || `QIT ${q.qit_id}`)
      .slice(0, 3);

    const totalQits = hasAnswersConfig?.qits?.length || 0;
    const remaining = Math.max(0, totalQits - 3);

    tooltipData.fields.questions = {
      name: 'Questions',
      value:
        questionNames.length > 0
          ? questionNames.join('\n') +
            (remaining > 0 ? `\n... and ${remaining} more` : '')
          : 'None',
    };

    const fileTypesOnly = (hasAnswersConfig?.qits || []).some(
      (q) => q.file_types_only
    );
    if (fileTypesOnly) {
      tooltipData.fields.fileTypesOnly = {
        name: 'File Types Only',
        value: 'Yes',
      };
    }

    tooltipData.fields.answersLevel = {
      name: 'Answers Level',
      value:
        hasAnswersConfig?.answers_level === 'certification'
          ? 'Certification'
          : 'Household Member',
    };

    if (hasAnswersConfig?.question_group_names?.length > 0) {
      tooltipData.fields.questionGroupNames = {
        name: 'Question Group Names',
        value: hasAnswersConfig.question_group_names.join(', '),
      };
    }

    if (hasAnswersConfig?.question_group_names_all) {
      tooltipData.fields.questionGroupNamesAll = {
        name: 'All Question Groups',
        value: 'Yes',
      };
    }

    if (hasAnswersConfig?.member_types_all) {
      tooltipData.fields.memberTypesAll = {
        name: 'Member Types',
        value: 'All',
      };
    } else if (hasAnswersConfig?.member_types?.length > 0) {
      const memberTypeNames = (hasAnswersConfig.member_types || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || el;
        })
        .join(', ');
      tooltipData.fields.memberTypes = {
        name: 'Member Types',
        value: memberTypeNames,
      };
    }

    const choiceOverrides = hasAnswersConfig?.choice_overrides;
    if (choiceOverrides) {
      if (choiceOverrides.yes_choice_id) {
        tooltipData.fields.yesChoiceId = {
          name: 'Yes Choice ID',
          value: choiceOverrides.yes_choice_id,
        };
      }
      if (choiceOverrides.no_choice_id) {
        tooltipData.fields.noChoiceId = {
          name: 'No Choice ID',
          value: choiceOverrides.no_choice_id,
        };
      }
      if (choiceOverrides.invert_symbol_render) {
        tooltipData.fields.invertSymbol = {
          name: 'Mark when has NO answers',
          value: 'Yes',
        };
      }
    }
  }

  if (type === TOOLTIP_TYPES.matchAnswers) {
    const matchAnswersConfig = data?.match_answers;
    tooltipData.label = 'Match Answers';

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data?.question_render_type,
    };

    const qitChoices = matchAnswersConfig?.qit_choices || {};
    const qitChoicesArray = Object.values(qitChoices);

    // Build separate arrays for questions and choices
    const questionLines = qitChoicesArray
      .map((item) => item.question_name)
      .slice(0, 3);

    const choiceLines = qitChoicesArray
      .map((item) => item.choice_names.join(', '))
      .slice(0, 3);

    const totalItems = qitChoicesArray.length;
    const remaining = Math.max(0, totalItems - 3);

    tooltipData.fields.qitNames = {
      name: 'Questions',
      value:
        questionLines.length > 0
          ? questionLines.join('\n') +
            (remaining > 0 ? `\n... and ${remaining} more` : '')
          : 'None',
    };

    tooltipData.fields.qitChoices = {
      name: 'Choices',
      value:
        choiceLines.length > 0
          ? choiceLines.join('\n') +
            (remaining > 0 ? `\n... and ${remaining} more` : '')
          : 'None',
    };

    tooltipData.fields.questionChoicesAll = {
      name: 'Question Choices All',
      value: matchAnswersConfig?.question_choices_all ? 'Yes' : 'No',
    };

    tooltipData.fields.answersLevel = {
      name: 'Answers Level',
      value:
        matchAnswersConfig?.answers_level === 'certification'
          ? 'Certification'
          : 'Household Member',
    };

    if (matchAnswersConfig?.member_types_all) {
      tooltipData.fields.memberTypesAll = {
        name: 'Member Types',
        value: 'All',
      };
    } else if (matchAnswersConfig?.member_types?.length > 0) {
      const memberTypeNames = (matchAnswersConfig.member_types || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || el;
        })
        .join(', ');
      tooltipData.fields.memberTypes = {
        name: 'Member Types',
        value: memberTypeNames,
      };
    }

    const choiceOverrides = matchAnswersConfig?.choice_overrides;
    if (choiceOverrides) {
      if (choiceOverrides.yes_choice_id) {
        tooltipData.fields.yesChoiceId = {
          name: 'Yes Choice ID',
          value: choiceOverrides.yes_choice_id,
        };
      }
      if (choiceOverrides.no_choice_id) {
        tooltipData.fields.noChoiceId = {
          name: 'No Choice ID',
          value: choiceOverrides.no_choice_id,
        };
      }
      if (choiceOverrides.invert_symbol_render) {
        tooltipData.fields.invertSymbol = {
          name: 'Map selected choices as Non-Selected',
          value: 'Yes',
        };
      }
    }
  }

  // TODO: Add Table Tooltip

  return tooltipData;
}

const getCalcAnnotName = (annotation) => {
  if (annotation?.calculation?.name) {
    return annotation.calculation.name;
  }
  return '';
};

export const getMappingTableCellName = (mapping) => {
  if (mapping.qits?.length > 0) {
    return mapping.qits
      .map((qit) => {
        return createMappingTableCellName(qit, mapping.qits_choices);
      })
      .join(' - ');
  }
  if (mapping.hhm_number) {
    return `Household Member Number`;
  }
  if (mapping.question_group_name) {
    return 'Question Group Name';
  }
  if (mapping.question_group_field) {
    return QuestionGroupFieldEnum[mapping.question_group_field];
  }
  if (mapping.household_member_field) {
    return HouseholdMemberFieldEnum[mapping.household_member_field];
  }
  if (mapping.calculation_name) {
    return 'Calculation Name';
  }
  if (mapping.calculation) {
    return mapping.calculation?.name;
  }
  return '';
};

const getHasAnswersAnnotName = (annotation) => {
  if (!annotation?.has_answers) {
    return null;
  }
  const config = annotation.has_answers;
  const scope = config.qit_scope === 'any' ? 'ANY' : 'ALL';
  const qitCount = config.qits?.length || 0;
  return `Has Answers [${scope}] (${qitCount} QITs)`;
};

const getMatchAnswersAnnotName = (annotation) => {
  if (!annotation?.match_answers) {
    return null;
  }
  const config = annotation.match_answers;
  const qitCount = Object.keys(config.qit_choices || {}).length;
  return `Match Answers (${qitCount} QIT${qitCount !== 1 ? 's' : ''})`;
};

export const buildAnnotations = (
  viewerInstance,
  data,
  configOverrides = {},
  householdMemberTypes = []
) => {
  if (!Array.isArray(data)) {
    return;
  }
  const annotationsToDraw = [];
  const annotationConfig = {
    FillColor: [238, 239, 241],
    TextColor: [84, 98, 110],
    StrokeColor: [218, 220, 222],
    Locked: false,
    LockedContents: true,
    NoRotate: true,
    NoResize: true,
    NoMove: true,
    ...configOverrides,
  };

  const groupedAnnotations = {};
  for (const annotation of data) {
    const freeText = new viewerInstance.Annotations.FreeTextAnnotation();
    freeText.PageNumber = annotation.page;
    const text =
      getAnnotationName({
        ...annotation.qit,
        contextName:
          annotation?.contextName ||
          annotation?.question_context?.context_template?.name ||
          null,
      }) ||
      getCalcAnnotName(annotation) ||
      getHasAnswersAnnotName(annotation) ||
      getMatchAnswersAnnotName(annotation) ||
      getMappingTableCellName(annotation) ||
      (annotation.is_initials ? 'Initials' : null) ||
      annotation.text;

    annotation.annotationId = freeText.Id;
    if (!annotation.text) {
      annotation.text = text || '';
    }

    freeText.setContents(text);
    freeText.X = annotation.x;
    freeText.Y = annotation.y;
    freeText.Width = annotation.width;
    freeText.Height = annotation.height;
    freeText.NoRotate = annotationConfig.NoRotate;
    freeText.NoResize = annotationConfig.NoResize;
    freeText.NoMove = annotationConfig.NoMove;
    freeText.FillColor = new viewerInstance.Annotations.Color(
      ...annotationConfig.FillColor
    );
    freeText.TextColor = new viewerInstance.Annotations.Color(
      ...annotationConfig.TextColor
    );
    freeText.StrokeColor = new viewerInstance.Annotations.Color(
      ...annotationConfig.StrokeColor
    );
    if (!annotation.mapping_table) {
      freeText.setPadding(new viewerInstance.Annotations.Rect(0, 0, 0, 0));
      freeText.FontSize = '9pt';
      freeText.TextVerticalAlign = 'bottom';
    }
    freeText.Locked = annotationConfig.Locked;
    freeText.LockedContents = annotationConfig.LockedContents;

    const tooltipData = buildTooltipData(
      annotation,
      getTooltipType(annotation),
      householdMemberTypes
    );

    let customData = {
      isProntoAnnotation: true,
      id: annotation?.id,
      annotationId: freeText.Id,
      text,
    };

    if (tooltipData && !annotation.mapping_table) {
      customData.tooltipData = tooltipData;
    }

    if (annotation.mapping_table) {
      customData = { ...customData, ...annotation };
    } else {
      customData = {
        ...customData,
        question_id: annotation?.question,
        mit: annotation?.mit?.id,
        sit: annotation?.sit?.id,
        is_date: annotation?.is_date || false,
        blank: annotation?.blank || false,
        is_name: annotation?.is_name || false,
        is_hhmt: annotation?.is_hhmt || false,
        is_phone: annotation?.is_phone || false,
        signature_reference: annotation?.signature_reference || null,
        qit: annotation?.qit?.id,
        multi_qits: annotation?.multi_qits,
        calculation: annotation?.calculation?.id || null,
        signatureId: annotation?.signatureId || undefined,
        question_context: annotation?.question_context?.id || null,
        signature_completed: annotation?.signature_completed || false,
        has_answers: annotation?.has_answers || null,
        match_answers: annotation?.match_answers || null,
      };
    }

    // PDF.js version 8
    freeText.setCustomData(CUSTOM_DATA_KEY, JSON.stringify(customData));
    const mappingId = annotation.mapping_table;
    if (mappingId) {
      if (!groupedAnnotations[mappingId]) {
        groupedAnnotations[mappingId] = [];
      }
      groupedAnnotations[mappingId].push(freeText);
    }
    annotationsToDraw.push(freeText);
  }

  viewerInstance.suppressAnnotationChanged = true;

  Object.values(groupedAnnotations).forEach((tableCellMappings) => {
    const tableConfig = parseJson(
      tableCellMappings[0].getCustomData(CUSTOM_DATA_KEY)
    ).mapping_table_data;

    const overflowIndicators = handleTableOverflowIndicators({
      mappingTableConfig: tableConfig,
      layout_config: tableConfig?.layout_config || [],
      annotations: tableCellMappings,
      overrides: configOverrides,
      Core: viewerInstance.Core,
    });

    const combinedAnnotations = [...tableCellMappings];

    if (tableConfig.mapping_orientation !== MAPPING_TABLE_ORIENTATIONS.CARD) {
      combinedAnnotations.push(...overflowIndicators);
    }

    viewerInstance.Core.annotationManager.groupAnnotations(
      combinedAnnotations[0],
      combinedAnnotations.slice(1)
    );

    annotationsToDraw.push(...overflowIndicators);
  });

  viewerInstance.suppressAnnotationChanged = false;

  viewerInstance.Core.annotationManager.addAnnotations(annotationsToDraw);
};

const getTooltipType = (annotation) => {
  if (annotation?.qit) {
    return TOOLTIP_TYPES.qit;
  }
  if (annotation?.sit) {
    return TOOLTIP_TYPES.sit;
  }
  if (annotation?.mit) {
    return TOOLTIP_TYPES.mit;
  }
  if (annotation?.calculation) {
    return TOOLTIP_TYPES.calculation;
  }
  if (annotation?.multi_qits?.length > 0) {
    return TOOLTIP_TYPES.multiQits;
  }
  if (annotation?.has_answers) {
    return TOOLTIP_TYPES.hasAnswers;
  }
  if (annotation?.match_answers) {
    return TOOLTIP_TYPES.matchAnswers;
  }
  return null;
};

export const mapAnnotationFromLibToJson = (annotation, overrides) => {
  const CustomData = parseJson(annotation.getCustomData(CUSTOM_DATA_KEY));

  return {
    x: annotation.X,
    y: annotation.Y,
    height: annotation.Height,
    width: annotation.Width,
    text: annotation?.text,
    page: annotation.PageNumber,
    localId: annotation?.Id || undefined, // local library id
    id: CustomData?.id || undefined, // backend id
    question_id: CustomData?.question_id || undefined,
    ...overrides,
  };
};

export function mapStoreMapping(item) {
  return {
    id: item?.id,
    qit: item?.qit?.id || null,
    multi_qits: item?.multi_qits || null,
    mit: item?.mit?.id || null,
    sit: item?.sit?.id || null,
    sit__name: item?.sit?.name,
    calculation: item?.calculation || null,
    signature_reference: item?.signature_reference || null,
    is_date: item?.is_date || false,
    blank: item?.blank || false,
    is_name: item?.is_name || false,
    is_hhmt: item?.is_hhmt || false,
    is_phone: item?.is_phone || false,
    compliance: item?.sit?.compliance || false,
    question: item?.question || null,
    choices: item?.choices || null,
    form_page: item?.form_page,
    section_name: item?.qit?.section?.name || null,
    form_page_name: item?.qit?.page?.name || null,
    question_group_name: item?.qit?.question_group?.name || null,
    question_group_render_type: item?.question_group_render_type || null,
    question_group_render_type_choice:
      item?.question_group_render_type_choice || null,
    question_group: item?.qit?.question_group?.id || null,
    question_group_type: item?.qit?.question_group?.type || null,
    household_member_type: item?.household_member_type || [],
    unassigned: item?.sit?.unassigned || false,
    question_context: item?.question_context?.id || null,
    contextName: item?.question_context?.context_template?.name || null,
    has_answers: item?.has_answers || null,
    match_answers: item?.match_answers || null,
  };
}

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  return ref.current;
}

export function parseJson(str) {
  let value = {};
  try {
    value = JSON.parse(str);
  } catch (e) {
    return value;
  }

  return value;
}

const filterCalc = (calc, search) =>
  (calc?.name || '').toLowerCase().includes(search);

export const buildQuestionnaireExpandedItems = (
  sections = [],
  mappings = []
) => {
  const toExpand = [];
  const qitsMapped = mappings.map((mapping) => mapping.qit);
  const questionnaireNode = `${
    TYPES.questionnaire
  }${DIVIDER}${0}${DIVIDER}${0}`;

  for (let x = 0; x < sections?.length; x++) {
    const section = sections[x];
    const sectionNode = `${TYPES.section}${DIVIDER}${section.id}${DIVIDER}${x}`;
    const pages = section.pages;

    for (let y = 0; y < pages.length; y++) {
      const page = section.pages[y];
      const pageNode = `${TYPES.page}${DIVIDER}${page.id}${DIVIDER}${y}`;
      const groups = page.question_groups;

      for (let z = 0; z < groups.length; z++) {
        const group = page.question_groups[z];
        const groupNode = `${TYPES.questionGroup}${DIVIDER}${
          group.id
        }${DIVIDER}${z}${DIVIDER}${DIVIDER}${JSON.stringify({
          sectionIndex: x,
          pageIndex: y,
        })}`;
        const resultQits = group?.questions.reduce(reduceQits, []);

        if (
          resultQits.some(
            (question) =>
              question.qit?.is_mapped &&
              question.qit?.id &&
              qitsMapped.includes(question.qit.id)
          )
        ) {
          if (!toExpand.includes(questionnaireNode)) {
            toExpand.push(questionnaireNode);
          }
          if (!toExpand.includes(sectionNode)) {
            toExpand.push(sectionNode);
          }
          if (!toExpand.includes(pageNode)) {
            toExpand.push(pageNode);
          }
          if (!toExpand.includes(groupNode)) {
            toExpand.push(groupNode);
          }
        }
      }
    }
  }

  return toExpand;
};

export const createTemporalTooltipData = (
  data,
  householdMemberTypes,
  qits,
  sits
) => {
  const tooltipData = {
    label: '',
    fields: {},
  };

  if (data.qit) {
    const isContext = Boolean(data.question_context);
    tooltipData.label = 'Questionnaire';
    const [question, path, assignee] = data.text.split('\n');
    tooltipData.fields.question = {
      name: !isContext ? 'Question' : 'Context name',
      value: question,
    };
    const isChoice = data.choices?.length > 0;
    if (isChoice) {
      const qit = qits.find((el) => el.id === data.qit);
      tooltipData.fields.question.value = qit?.question?.name || '';
      tooltipData.fields.choices = {
        name: 'Choices',
        value: data.text,
      };
      if ('blank' in data) {
        tooltipData.fields.blank = {
          name: 'Non-Selected',
          value: data.blank ? 'Yes' : 'No',
        };
      }
      if (qit?.section) {
        tooltipData.fields.section = {
          name: 'Section',
          value: qit?.section?.name,
        };
      }
      if (qit?.page) {
        tooltipData.fields.page = {
          name: 'Page',
          value: qit.page.name,
        };
      }
      if (qit?.question_group) {
        tooltipData.fields.group = {
          name: 'Group',
          value: qit.question_group.name,
        };
      }
    }

    if (path && !isChoice) {
      const parts = path.split(' / ');
      parts.forEach((part, index) => {
        let section = 'Section';
        if (index === 1) {
          section = 'Page';
        }
        if (index === 2) {
          section = 'Group';
        }
        tooltipData.fields[section] = {
          name: section,
          value: part,
        };
      });
    }

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data.render_type,
    };
    tooltipData.fields.groupType = {
      name: 'Group Type',
      value: data.groupType,
    };
    if (data.question_group_render_type) {
      tooltipData.fields.groupRenderType = {
        name: 'Group Render Type',
        value:
          GROUP_RENDER_TYPES[data.question_group_render_type]?.displayName ||
          data.question_group_render_type,
      };
    }
    if (assignee) {
      tooltipData.fields.for = {
        name: 'For',
        value: assignee.replace('For ', ''),
      };
    }
    return tooltipData;
  }
  if (data.mitBody) {
    tooltipData.label = 'Miscellaneous';
    tooltipData.fields.name = {
      name: 'Field',
      value: data.mitBody?.field_name || '',
    };
    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data.render_type,
    };
    return tooltipData;
  }
  if (data.sit) {
    const sit = sits.find((el) => el.id === data.sit);
    tooltipData.label = 'eSign';
    tooltipData.fields.name = { name: 'Name', value: sit?.name || '' };
    tooltipData.fields.isDate = {
      name: 'Is signature date',
      value: data?.is_date ? 'Yes' : 'No',
    };
    tooltipData.fields.isName = {
      name: 'Is signature name',
      value: data?.is_name ? 'Yes' : 'No',
    };
    tooltipData.fields.isHhmt = {
      name: 'Is signature member type',
      value: data?.is_hhmt ? 'Yes' : 'No',
    };
    tooltipData.fields.isPhone = {
      name: 'Is signature phone number',
      value: data?.is_phone ? 'Yes' : 'No',
    };
    tooltipData.fields.signatureCollection = {
      name: 'Signature Collection',
      value: sit?.signature_collection,
    };
    tooltipData.fields.signatureType = {
      name: 'Signature Type',
      value: sit?.signature_type,
    };
    tooltipData.fields.compliance = {
      name: 'Compliance',
      value: sit?.compliance ? 'Yes' : 'No',
    };
    tooltipData.fields.unassigned = {
      name: 'Unassigned',
      value: sit?.unassigned ? 'Yes' : 'No',
    };
    tooltipData.fields.hhmt = {
      name: 'Needed Signature',
      value: (sit?.needed_signatures || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el.id);
          return found?.display_name || found?.name || '';
        })
        .join(', '),
    };
    return tooltipData;
  }
  if (data.calculation) {
    tooltipData.label = 'Calculation';
    tooltipData.fields.name = { name: 'Name', value: data.text };
    tooltipData.fields.formula = {
      name: 'Formula',
      value: 'Formula will appear after saving the mapping',
    };
    tooltipData.fields.hhmt = {
      name: 'Household Member Types',
      value: (data?.household_member_type || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || '';
        })
        .join(', '),
    };
    tooltipData.fields.groupRenderType = {
      name: 'Group Render Type',
      value: data?.question_group_render_type_choice,
    };
    return tooltipData;
  }
  if (data.has_answers) {
    const hasAnswersConfig = data.has_answers;
    tooltipData.label = 'Has Answers';

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data.render_type,
    };

    tooltipData.fields.qitScope = {
      name: 'QIT Scope',
      value:
        hasAnswersConfig.qit_scope === 'any'
          ? 'Any (OR logic)'
          : 'All (AND logic)',
    };

    const questionNames = (hasAnswersConfig.qits || [])
      .map((q) => q.question_name || `QIT ${q.qit_id}`)
      .slice(0, 3);

    const totalQits = hasAnswersConfig.qits?.length || 0;
    const remaining = Math.max(0, totalQits - 3);

    tooltipData.fields.questions = {
      name: 'Questions',
      value:
        questionNames.length > 0
          ? questionNames.join('\n') +
            (remaining > 0 ? `\n... and ${remaining} more` : '')
          : 'None',
    };

    const fileTypesOnly = (hasAnswersConfig.qits || []).some(
      (q) => q.file_types_only
    );
    if (fileTypesOnly) {
      tooltipData.fields.fileTypesOnly = {
        name: 'File Types Only',
        value: 'Yes',
      };
    }

    tooltipData.fields.answersLevel = {
      name: 'Answers Level',
      value:
        hasAnswersConfig.answers_level === 'certification'
          ? 'Certification'
          : 'Household Member',
    };

    if (hasAnswersConfig.member_types_all) {
      tooltipData.fields.memberTypesAll = {
        name: 'Member Types',
        value: 'All',
      };
    } else if (hasAnswersConfig.member_types?.length > 0) {
      const memberTypeNames = (hasAnswersConfig.member_types || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || el;
        })
        .join(', ');
      tooltipData.fields.memberTypes = {
        name: 'Member Types',
        value: memberTypeNames,
      };
    }

    const choiceOverrides = hasAnswersConfig.choice_overrides;
    if (choiceOverrides?.invert_symbol_render) {
      tooltipData.fields.invertSymbol = {
        name: 'Mark when has NO answers',
        value: 'Yes',
      };
    }

    return tooltipData;
  }
  if (data.match_answers) {
    const matchAnswersConfig = data.match_answers;
    tooltipData.label = 'Match Answers';

    tooltipData.fields.renderType = {
      name: 'Render Type',
      value: data.render_type,
    };

    const enrichedChoices = matchAnswersConfig.qit_choices_enriched || {};
    const lines = Object.values(enrichedChoices)
      .map((item) => `${item.question_name}: ${item.choice_names.join(', ')}`)
      .slice(0, 3);

    const totalItems = Object.keys(enrichedChoices).length;
    const remaining = Math.max(0, totalItems - 3);

    tooltipData.fields.qitChoices = {
      name: 'Question Choices',
      value:
        lines.length > 0
          ? lines.join('\n') +
            (remaining > 0 ? `\n... and ${remaining} more` : '')
          : 'None',
    };

    tooltipData.fields.questionChoicesAll = {
      name: 'Question Choices All',
      value: matchAnswersConfig.question_choices_all ? 'Yes' : 'No',
    };

    tooltipData.fields.answersLevel = {
      name: 'Answers Level',
      value:
        matchAnswersConfig.answers_level === 'certification'
          ? 'Certification'
          : 'Household Member',
    };

    if (matchAnswersConfig.member_types_all) {
      tooltipData.fields.memberTypesAll = {
        name: 'Member Types',
        value: 'All',
      };
    } else if (matchAnswersConfig.member_types?.length > 0) {
      const memberTypeNames = (matchAnswersConfig.member_types || [])
        .map((el) => {
          const found = householdMemberTypes.find((hhmt) => hhmt.id === el);
          return found?.display_name || found?.name || el;
        })
        .join(', ');
      tooltipData.fields.memberTypes = {
        name: 'Member Types',
        value: memberTypeNames,
      };
    }

    const choiceOverrides = matchAnswersConfig.choice_overrides;
    if (choiceOverrides?.invert_symbol_render) {
      tooltipData.fields.invertSymbol = {
        name: 'Map selected choices as Non-Selected',
        value: 'Yes',
      };
    }

    return tooltipData;
  }
};

export const buildQuestionCalcsOptions = (qits) => {
  const questionCalcsOptions = [];

  qits
    .filter((q) =>
      VALID_CALC_SOURCE_QUESTION_TYPES.includes(q.question?.question_type)
    )
    .forEach((qit) => {
      if (qit.parent_question) {
        if (qits[qit.parent_question].question.question_type !== 'file') {
          questionCalcsOptions.push({
            ...qit,
            optionGroup: `Questions of ${qit.question_group?.name}`,
          });
        }
      } else {
        questionCalcsOptions.push({
          ...qit,
          optionGroup: `Questions of ${qit.question_group?.name}`,
        });
      }
    });

  return questionCalcsOptions.sort((a, b) => {
    const bQuestion = `${b.question_group?.name || ''} ${
      b.question?.name || ''
    }`;
    const aQuestion = `${a.question_group?.name || ''} ${
      a.question?.name || ''
    }`;
    return -bQuestion.localeCompare(aQuestion);
  });
};
